import { DesignMaterialPath } from "common/materials/library/designMaterialPath";
import { MaterialTemplateIdToJson } from "common/materials/library/materialTemplateIdToJson";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialCacheKey } from "common/materials/materialSrc";
import type {
  WebGLRenderer,
  MeshPhysicalMaterial,
  MeshPhysicalMaterialParameters,
  Material,
  Texture as TextureType,
  PixelFormat,
} from "three";
import { BackSide, DoubleSide, FrontSide, RepeatWrapping, Texture } from "three";
import { HTTPMaterialParameterLoader } from "./HTTPMaterialParameterLoader";
import { DebugMaterialColors, MeshHardcodedMaterialType } from "./MaterialFactory";
import {
  includeAdvancedPCFShadows,
  includeCommonMaterialUniforms,
  includeLightProbes,
  includeScreenspaceFX,
  includeSsfxRendererOutput,
} from "./materials/customizeShader";
import { createMaterialForParameters, designMaterialNew, setDesignMaterialOptions } from "./materials/designMaterial";
import { DEFAULT_TEXTURE_ANISOTROPY } from "./materials/designMaterialDefaults";
import type { DesignMaterialLoadOptions } from "./materials/designMaterialRuntimeConfig";
import {
  BakeColorTextureMode,
  DetailRenderMode,
  defaultDesignMaterialLoadOptions,
} from "./materials/designMaterialRuntimeConfig";
import { logMaterialSrc } from "./materials/logMaterialSrc";
import {
  finishMaterialLoadDiagnostic,
  finishTextureLoadDiagnostic,
  markMaterialLoadDesignMaterialNewEnd,
  markMaterialLoadDesignMaterialNewStart,
  markMaterialLoadParamsEnd,
  markMaterialLoadParamsStart,
  recordMaterialLoadReadyWait,
  startMaterialLoadDiagnostic,
  startTextureLoadDiagnostic,
} from "./materials/materialLoadDiagnostics";
import THREEAssetLoader from "./THREEAssetLoader";
import { checkWebGLContextLost } from "./threejs/higharc/checkWebGLContextLost";
import { assignSharedDefines, MaterialSharedParameterGroup } from "./threejs/higharc/MaterialSharedParameterGroup";
import type { ExtendedMaterial } from "./threejs/three-extensions";

const failureMaterialType = MeshHardcodedMaterialType.DEBUG_WHITE_MESH;
type OnBeforeCompileShader = Parameters<Material["onBeforeCompile"]>[0];

function loadedMaterialCacheKey(src: MaterialSrc, hasSSFXRenderer: boolean) {
  return materialCacheKey(src) + ";" + (hasSSFXRenderer ? 1 : 0);
}

interface MaterialCache {
  material: Promise<Material>;
  materialSrc: MaterialSrc;
  hasSSFXRenderer: boolean;
  state: "pending" | "rejected" | "fulfilled";
}

interface TextureCacheEntry {
  texture: TextureType;
  loadedPromise: Promise<void>;
  state: "pending" | "rejected" | "fulfilled";
}

export class HTTPMaterialFactory {
  private readonly designMaterialCache: { [key: string]: MaterialCache } = {};
  private readonly hardcodedMaterials: { [key: string]: Material } = {};
  private readonly textureCache: { [key: string]: TextureCacheEntry } = {};

  readonly defaultMaterialParams = Object.freeze({
    opacity: 1,
    transparent: false,
    color: 0xeeeeee,
    roughness: 1.0,
    metalness: 0.0,
    reflectivity: 0.0,
    side: FrontSide,
    clipShadows: false,
    shadowSide: DoubleSide,
  });

  private readonly assetLoader: THREEAssetLoader;
  private readonly materialLoader: HTTPMaterialParameterLoader;

  // Debug settings
  private readonly disableDesignMaterialSDF: boolean = false;
  private readonly disableDesignMaterialLayers: boolean = false;

  private bakeColorTexture: BakeColorTextureMode = BakeColorTextureMode.DONT_BAKE;

  // We need to asynchronously set the fetchBakedColorTextures flag that affects material loading before the HTTPMaterialFactory can be used.
  // The markReady() function is used to signal that the parameters have been set and we can start loading materials.
  setBakeColorTexture(bakeColorTexture: BakeColorTextureMode) {
    if (this.bakeColorTexture !== bakeColorTexture) {
      this.bakeColorTexture = bakeColorTexture;
      void this.updateLoadOptions();
    }
    this.markReady();
  }

  private readyPromise: Promise<void>;
  private readyPromiseResolve: (() => void) | undefined;
  private markReady() {
    if (this.readyPromiseResolve) {
      this.readyPromiseResolve();
      this.readyPromiseResolve = undefined;
    }
  }

  // The renderer is needed to generate height maps used by design materials.
  constructor(
    private readonly renderer: WebGLRenderer,
    waitForMarkReady: boolean, // Should only be set to false in internal tools that don't depend on asynchronously loaded performance tier parameters.
    private readonly useWebgl2TextureAtlas: boolean = true,
    private readonly enableTopDownLightOffsetMap: boolean = false,
    private readonly offsetMapLightCount: number = 8,
    private forceDetailRenderMode: "SDF" | "ParallaxMap" | null = null,
  ) {
    this.assetLoader = THREEAssetLoader.instance;
    this.materialLoader = HTTPMaterialParameterLoader.instance;

    this.readyPromise = new Promise<void>((resolve) => {
      this.readyPromiseResolve = resolve;
    });
    if (!waitForMarkReady) {
      this.markReady();
    }

    this.hardcodedMaterials[MeshHardcodedMaterialType.INVISIBLE_SHADOW_MESH] = this.buildMeshPhysicalMaterial({
      ...this.defaultMaterialParams,
      side: BackSide,
      colorWrite: false,
      depthWrite: false,
    });
    this.hardcodedMaterials[MeshHardcodedMaterialType.DEBUG_RED_MESH] = this.buildMeshPhysicalMaterial({
      ...this.defaultMaterialParams,
      color: 0xff0000,
    });
    this.hardcodedMaterials[MeshHardcodedMaterialType.DEBUG_WHITE_MESH] = this.buildMeshPhysicalMaterial({
      ...this.defaultMaterialParams,
      color: 0xffffff,
    });
    this.hardcodedMaterials[MeshHardcodedMaterialType.DESELECTED_OVERRIDE] = this.buildMeshPhysicalMaterial({
      color: 0x7b7b7b,
      emissive: 0xffffff,
      emissiveIntensity: 0.1,
    });
  }

  private buildMeshPhysicalMaterial(props: MeshPhysicalMaterialParameters, disableSSAO?: boolean) {
    const material = createMaterialForParameters(props);
    assignSharedDefines(MaterialSharedParameterGroup.instance, material);

    if (props.color !== undefined) {
      material.color.set(props.color);
    }
    if (props.colorWrite !== undefined) {
      material.colorWrite = props.colorWrite;
    }
    if (props.depthWrite !== undefined) {
      material.depthWrite = props.depthWrite;
    }
    if (props.side !== undefined) {
      material.side = props.side;
    }
    if (props.emissive !== undefined) {
      material.emissive.set(props.emissive);
    }
    if (props.emissiveIntensity !== undefined) {
      material.emissiveIntensity = props.emissiveIntensity;
    }

    material.onBeforeCompile = (shader: OnBeforeCompileShader) => {
      includeCommonMaterialUniforms(shader);

      includeAdvancedPCFShadows(shader);

      includeScreenspaceFX(shader);

      includeLightProbes(shader);

      includeSsfxRendererOutput(shader);
    };

    (material as unknown as ExtendedMaterial).onProgramChange = (params: { uniforms: { [name: string]: any } }) => {
      MaterialSharedParameterGroup.instance.useSharedUniformsInMaterial(params.uniforms);
    };

    if (disableSSAO && material.defines) {
      material.defines["DISABLE_SSAO"] = "";
    }
    return material;
  }

  getHardcodedMaterial(id: MeshHardcodedMaterialType) {
    return this.hardcodedMaterials[id];
  }

  designMaterialIsFetched(materialId: MaterialSrc, hasSSFXRenderer: boolean): boolean {
    const cacheKey = loadedMaterialCacheKey(materialId, hasSSFXRenderer);
    return this.designMaterialCache[cacheKey]?.state === "fulfilled";
  }

  /**
   * Get a material with one of the predefined debug colors.
   * @param colorId One of the colors in DebugMaterialColors.
   * @returns A design material with the given color.
   */
  async getDesignMaterialWithColor(colorId: number, hasSSFXRenderer: boolean): Promise<Material | undefined> {
    console.assert(DebugMaterialColors.includes(colorId));
    return await this.getDesignMaterial({ ...DesignMaterialPath.Color.C, colorId: colorId }, hasSSFXRenderer);
  }

  private getLoadOptions(): DesignMaterialLoadOptions {
    let forceDetailRenderMode: DetailRenderMode | undefined = undefined;
    if (this.forceDetailRenderMode !== null) {
      forceDetailRenderMode = DetailRenderMode[this.forceDetailRenderMode];
    }

    return {
      ...defaultDesignMaterialLoadOptions,
      disableSDF: this.disableDesignMaterialSDF,
      disableLayers: this.disableDesignMaterialLayers,
      enableLightOffsetMap: this.enableTopDownLightOffsetMap,
      offsetMapLightCount: this.offsetMapLightCount,
      forceDetailRenderMode,
      bakeColorTexture: this.bakeColorTexture,
      useWebgl2TextureAtlas: this.useWebgl2TextureAtlas,
    };
  }

  async getDesignMaterial(
    materialSrc: MaterialSrc,
    hasSSFXRenderer: boolean,
    forceRefresh = false,
    waitForFullLoad = true,
  ): Promise<Material | undefined> {
    const cacheKey = loadedMaterialCacheKey(materialSrc, hasSSFXRenderer);
    if (materialSrc === null) {
      return undefined;
    }

    if (!forceRefresh && this.designMaterialCache.hasOwnProperty(cacheKey)) {
      const cached = this.designMaterialCache[cacheKey];
      return waitForFullLoad ? this.materialWhenFullyLoaded(cached) : cached.material;
    }

    // Capture the entry locally so the async callbacks below (especially the long-lived
    // fullyLoadedPromise.then) always update THIS entry, even if a concurrent forceRefresh call
    // replaces this.designMaterialCache[cacheKey] with a new entry while textures are still loading.
    // material is assigned in a second step so the closure can safely reference cacheEntry.
    const cacheEntry: MaterialCache = {
      state: "pending",
      hasSSFXRenderer,
      materialSrc,
      material: Promise.resolve(this.getHardcodedMaterial(failureMaterialType)),
    };
    this.designMaterialCache[cacheKey] = cacheEntry;
    cacheEntry.material = (async () => {
      const diagnostic = startMaterialLoadDiagnostic({ materialSrc, hasSSFXRenderer });
      try {
        const paramsStartTimeMs = markMaterialLoadParamsStart(diagnostic);
        const params = await this.materialLoader.fetchDesignMaterialParams(materialSrc);
        markMaterialLoadParamsEnd(diagnostic, paramsStartTimeMs);
        const readyStartTimeMs = performance.now();
        await this.readyPromise; // Make sure that the settings for fetching baked textures are set.
        recordMaterialLoadReadyWait(diagnostic, performance.now() - readyStartTimeMs);
        if (params === null) {
          console.error("Could not make design material", logMaterialSrc(materialSrc));
          cacheEntry.state = "rejected";
          finishMaterialLoadDiagnostic(diagnostic, "rejected");
          return this.getHardcodedMaterial(failureMaterialType);
        }
        const designMaterialNewStartTimeMs = markMaterialLoadDesignMaterialNewStart(diagnostic);
        // The cached promise always resolves once the material is usable; texture loads finish in
        // the background. Callers that need every texture re-await userData.fullyLoadedPromise via
        // materialWhenFullyLoaded, so a shared cache entry never blocks usable-stage consumers.
        const material = await designMaterialNew(
          params,
          this.renderer,
          (url, overrideFormat) => this.getTextureForMaterial(cacheKey, url, overrideFormat),
          hasSSFXRenderer,
          materialSrc,
          this.getLoadOptions(),
          cacheKey,
          false,
        );
        markMaterialLoadDesignMaterialNewEnd(diagnostic, designMaterialNewStartTimeMs);

        const templateJSONPath = MaterialTemplateIdToJson[materialSrc.templateId];
        material.name = templateJSONPath.replace(/^.*[\\\/]/, "");
        const fullyLoadedPromise = material.userData.fullyLoadedPromise;
        if (fullyLoadedPromise instanceof Promise) {
          void fullyLoadedPromise.then(
            () => {
              cacheEntry.state = "fulfilled";
              finishMaterialLoadDiagnostic(diagnostic, "fulfilled");
            },
            () => {
              cacheEntry.state = "rejected";
              finishMaterialLoadDiagnostic(diagnostic, "rejected");
            },
          );
        } else {
          cacheEntry.state = "fulfilled";
          finishMaterialLoadDiagnostic(diagnostic, "fulfilled");
        }
        return material;
      } catch (e) {
        checkWebGLContextLost(this.renderer, e, "Could not make design material." + logMaterialSrc(materialSrc));
        cacheEntry.state = "rejected";
        finishMaterialLoadDiagnostic(diagnostic, "rejected");
        return this.getHardcodedMaterial(failureMaterialType);
      }
    })();

    const created = cacheEntry;
    return waitForFullLoad ? this.materialWhenFullyLoaded(created) : created.material;
  }

  /**
   * Resolve a cache entry once all of its texture loads have settled, preserving the historical
   * contract that a fully-awaited design material either has every texture or is the failure material.
   */
  private async materialWhenFullyLoaded(cacheEntry: MaterialCache): Promise<Material> {
    const material = await cacheEntry.material;
    const fullyLoadedPromise = material.userData?.fullyLoadedPromise;
    if (fullyLoadedPromise instanceof Promise) {
      try {
        await fullyLoadedPromise;
      } catch {
        return this.getHardcodedMaterial(failureMaterialType);
      }
    }
    return material;
  }

  private async updateLoadOptions() {
    const loadOptions = this.getLoadOptions();
    const promises: Promise<void>[] = [];
    for (const materialCache of Object.values(this.designMaterialCache)) {
      const material = await materialCache.material;
      // Let in-flight texture loads settle so the fulfilled/rejected state below is final.
      const fullyLoadedPromise = material.userData?.fullyLoadedPromise;
      if (fullyLoadedPromise instanceof Promise) {
        await fullyLoadedPromise.catch(() => undefined);
      }
      // Skip if failureMaterialType
      if (materialCache.state === "fulfilled") {
        promises.push(
          setDesignMaterialOptions(
            material as MeshPhysicalMaterial,
            this.renderer,
            materialCache.hasSSFXRenderer,
            this.getTexture.bind(this),
            materialCache.materialSrc,
            loadOptions,
          )[1],
        );
      }
    }
    await Promise.all(promises);
  }

  initGUI(gui, onChange: () => void) {
    if (!gui) {
      return;
    }

    const materialLoadGUI = gui.addFolder("Material load options");
    materialLoadGUI.add(this, "forceDetailRenderMode", [null, "SDF", "ParallaxMap"]).onChange(async () => {
      await this.updateLoadOptions();
      onChange();
    });
    materialLoadGUI
      .add(this, "bakeColorTexture", [
        BakeColorTextureMode.DONT_BAKE,
        BakeColorTextureMode.REPEATING_LAYERS,
        BakeColorTextureMode.FETCH_FROM_AWS,
        BakeColorTextureMode.BAKE,
        BakeColorTextureMode.FLAT_COLOR,
      ])
      .onChange(async () => {
        await this.updateLoadOptions();
        onChange();
      });
  }

  /**
   * @returns The texture and a promise that will complete once the texture has actually finished loading.
   */
  private getTexture(url: string, overrideFormat?: PixelFormat): [TextureType | null, Promise<void>] {
    return this.getTextureForMaterial(null, url, overrideFormat);
  }

  private getTextureForMaterial(
    materialKey: string | null,
    url: string,
    overrideFormat?: PixelFormat,
  ): [TextureType | null, Promise<void>] {
    if (!url) {
      return [null, new Promise<void>(() => {})];
    }

    if (this.textureCache.hasOwnProperty(url)) {
      const cacheEntry = this.textureCache[url];
      const diagnostic = startTextureLoadDiagnostic({
        cacheStatus: cacheEntry.state === "pending" ? "pending" : "hit",
        materialKey,
        overrideFormat: overrideFormat ?? null,
        url,
      });
      void cacheEntry.loadedPromise.then(
        () => finishTextureLoadDiagnostic(diagnostic, "fulfilled"),
        () => finishTextureLoadDiagnostic(diagnostic, "rejected"),
      );
      return [cacheEntry.texture, cacheEntry.loadedPromise];
    }

    const diagnostic = startTextureLoadDiagnostic({
      cacheStatus: "miss",
      materialKey,
      overrideFormat: overrideFormat ?? null,
      url,
    });
    const texture = new Texture();
    texture.wrapS = RepeatWrapping;
    texture.wrapT = RepeatWrapping;
    texture.anisotropy = DEFAULT_TEXTURE_ANISOTROPY;
    const cacheEntry: TextureCacheEntry = {
      texture,
      loadedPromise: Promise.resolve(),
      state: "pending",
    };
    const loadedPromise = new Promise<void>((resolve, reject) => {
      void (async () => {
        await this.assetLoader.loadTextureDirect({
          texture,
          url,
          onLoad: () => {
            cacheEntry.state = "fulfilled";
            finishTextureLoadDiagnostic(diagnostic, "fulfilled");
            resolve();
          },
          onError: () => {
            cacheEntry.state = "rejected";
            console.error("could not load texture: " + url);
            finishTextureLoadDiagnostic(diagnostic, "rejected");
            reject();
          },
          overrideFormat,
        });
      })();
    });
    cacheEntry.loadedPromise = loadedPromise;
    this.textureCache[url] = cacheEntry;
    return [texture, loadedPromise];
  }
}
